/**
 * Practice documents — the staff-side request lifecycle client
 * (`tasks/practice-documents.md` §3.3, §7 phase 2).
 *
 * The sibling of `patient-form-request.service.ts`, for the OTHER payload. The two features sit
 * beside each other (§0 decision 1) and their tables are separate, so their clients are separate
 * too — but the lifecycle they speak to is one implementation (`utils/patientRequestDelivery.js`,
 * §12), which is why the dedupe codes and the resend cooldown below are the same three words the
 * forms service already renders.
 *
 * Consumed by `PatientDocumentListComponent` (the patient-file panel) and
 * `PatientDocumentSendDialogComponent` (the manual send).
 *
 * ── WHAT IS DELIBERATELY NOT HERE ────────────────────────────────────────────────────────────
 *
 * There is NO copy-link method and no place a `tokenDigest` could land. §5 stores only
 * `sha256(token)`, so the raw link is unrecoverable by anything including the server, and a resend
 * MINTS A NEW TOKEN — the previously sent link stops working the instant it does. Anything on this
 * class that looked like "get me the link" would be a lie, and `PatientDocumentRequestDto` has no
 * field a digest could arrive in.
 *
 * ── WHAT IS HERE THAT THE FORMS SERVICE HAS NO EQUIVALENT OF ─────────────────────────────────
 *
 * `reviewRequest`. §0 decision 3 is that verifying a returned document is a PERSON's job, not a
 * fingerprint check — so every return lands on `awaiting_review` and a practice user accepts it or
 * rejects it with a note. That is the distinctive part of this feature and it has no counterpart
 * on the forms side, where a submitted answer set is simply filed.
 *
 * ── TWO STATUSES MEAN "STILL WAITING", NOT ONE ───────────────────────────────────────────────
 *
 * §3.3: `scheduled` (created, `dueAt` not yet arrived — Phase 4) and `sent` (the patient has it).
 * Collapsing them would make an ask nobody has been given indistinguishable from one the patient
 * has ignored. Phase 2 only ever creates `sent` rows, but the panel renders `scheduled` distinctly
 * from the day Phase 4 lands rather than being retrofitted then.
 */

import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, switchMap, tap } from 'rxjs';
import { withPdfPages$ } from '../pdf-pages/with-pdf-pages';

import { PatientService } from './patient.service';
import { SocketService } from './socket.service';

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * Types — the `PatientDocumentRequests` row of §3.3 as it reaches a staff screen
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * §3.3 plus §14.4's `delivered`.
 *
 * `delivered` IS NOT AN OUTSTANDING ASK. §14.2 makes an information-only document one the patient
 * is never asked to return: it is attached to the email, there is no link, nothing comes back and
 * nothing is reviewed. §14.4 still creates a request row for it — so that the patient-file panel
 * answers *"what has this practice sent this patient"* uniformly rather than half from requests and
 * half from communications, and so a subject-access export carries it — but that row lands in a
 * TERMINAL state and is excluded from `OPEN_DOCUMENT_STATUSES` below.
 *
 * It therefore needs a label of its own everywhere a status is drawn. Reusing `sent` would put an
 * information sheet in the same queue as a form the practice is waiting on, which is precisely the
 * §3.3 mistake (`scheduled` vs `sent`) repeated one row further along.
 */
export type PatientDocumentRequestStatus =
  | 'scheduled' | 'sent' | 'in_progress' | 'completed' | 'expired' | 'revoked' | 'delivered';

/**
 * §0 decision 3, §26. NULL until something comes back — "not returned" is not "not reviewed".
 *
 * `accepted` IS NOT AN END STATE any more. It means "signed off as the right document, waiting on
 * a clinician", and a row in it is deliberately NOT on the patient's timeline. `validated` is the
 * only state the timeline draws.
 */
export type PatientDocumentReviewStatus =
  | 'awaiting_review' | 'accepted' | 'rejected' | 'validated';

/** The `PracticeDocument` include — the blank this request asked for. */
export interface PatientDocumentRequestDocument {
  id: string;
  name: string;
  description?: string | null;
  category?: string | null;
  mediaFileName?: string | null;
  fillMode?: string | null;
}

/**
 * One returned file, as `GET /patient-document-requests/:id` serves it in `returnedFiles`.
 *
 * A CHILD TABLE, not a column (§3.4), because the realistic return is a phone photograph per page —
 * so the panel always renders a list, never "the file".
 *
 * ── TWO FIELDS THAT LOOK LIKE DETAIL AND ARE NOT ────────────────────────────────────
 *
 * `url` is SHORT-LIVED and minted per request, like the blank's: `mediaUrl` is stored unsigned
 * (§10.1 decision 3). It is `null` when the stored reference could not be parsed — the server logs
 * that and still returns the row, deliberately, because dropping it would hide from a reviewer that
 * a page exists and cannot be opened.
 *
 * `active` is FALSE for a file superseded by a rejection. A rejection RE-OPENS the same request and
 * deactivates what came back rather than deleting it, so `returnedFiles` legitimately carries both
 * generations and `returnedFileCount` counts only the live ones. The panel must say which is which;
 * rendering them as one list would show a reviewer pages the patient has already replaced.
 */
export interface PatientDocumentReturnedFileDto {
  id: string;
  mediaFileName?: string | null;
  mediaType?: string | null;
  displayIndex?: number | null;
  active?: boolean;
  /** Short-lived, minted per request. `null` when the blob reference is unusable. */
  url?: string | null;
  expiresInMinutes?: number | null;
  expiresAt?: string | null;
}

/**
 * The staff DTO.
 *
 * NOTE WHAT IS ABSENT: there is no `tokenDigest` and no raw token, and neither should ever be
 * added. `sentMediaFileName` (§11) IS here — it is what the patient's download was called, and a
 * reviewer holding a returned scan needs to know which blank it was completed against, since
 * `POST /practice-documents/:id/file` can replace the blank underneath an outstanding request.
 */
export interface PatientDocumentRequestDto {
  id: string;
  groupId: string;
  patientId: string;
  practiceDocumentId: string;
  requestedByUserId?: string | null;
  physicianId?: string | null;
  source: string;
  status: PatientDocumentRequestStatus;

  /** §3.3: `scheduled` rows exist before they are sent, so these are genuinely different dates. */
  dueAt?: string | null;
  sentAt?: string | null;
  returnedAt?: string | null;

  /**
   * WHO RETURNED IT — `tasks/document-rejection-resend.md` §7.
   *
   * `'patient'` is the invite rail. `'practice'` means somebody in the practice filed it, because
   * the patient emailed the form back or brought a printed copy. NULL means nothing has been
   * returned against this row.
   *
   * A reviewer must be able to tell which they are holding: a document typed in on a patient's
   * behalf is a different fact from one the patient sent, and the review is otherwise identical.
   */
  returnSource?: 'patient' | 'practice' | null;
  /** The member of staff who filed it, when `returnSource` is `'practice'`. */
  returnedByUserId?: string | null;
  returnedByName?: string | null;

  /** §11 — the blank AS SENT. NULL means "fall back to the document's current file". */
  sentMediaFileName?: string | null;
  sentFillMode?: string | null;

  reviewStatus?: PatientDocumentReviewStatus | null;
  reviewedByUserId?: string | null;
  reviewedAt?: string | null;
  reviewNote?: string | null;

  /**
   * Plaintext derived columns (§3.3), written by §6's AI read since Phase 6.
   *
   * BOTH ARE NULLABLE AND NULL IS NOT ZERO. Most documents do not score at all, so NULL/NULL is
   * the ordinary case; a genuine 0 out of 10 is a different fact and both have to survive to the
   * screen. `clinical-timeline-documents.documentScoreLabel` is the one place that decides how
   * each renders, and it returns `null` — draw nothing — rather than a zero.
   */
  scoreTotal?: number | null;
  scoreMax?: number | null;

  /**
   * §6 item 2 — WHETHER THERE IS AN AI READ. Not the read itself.
   *
   * `aiSummary` WAS DECLARED HERE and is gone — 9 Sep 2026, owner's call. The patient-file panel's
   * "AI SUMMARY" block was removed entirely (the read feeds Cura, which loads its own context), so
   * nothing on any screen renders the text. `GET /patient-document-requests/:id` still SERVES it
   * and the server still writes the column; a field on this interface that no template and no
   * method reads is exactly the `startedAt` defect this feature removed a commit earlier, and the
   * cost of declaring it is a reader who assumes something displays it.
   *
   * `hasAiSummary` STAYS, and is read — by `clinical-timeline-documents.toTimelineDocuments`,
   * which maps THIS interface and whose row says ", with an AI summary". The CLINICAL TIMELINE is
   * a different section and was not part of that change. The same is true of the two score columns
   * above: the panel stopped drawing them, the timeline did not.
   */
  hasAiSummary?: boolean;

  /**
   * THE INVITE THIS DOCUMENT WAS DELIVERED ON — `tasks/practice-documents.md` §19, §21.1.
   *
   * The resend affordance in the patient-file panel is addressed to THIS, not to the request:
   * documents ride the `PatientInvite` rail and a resend re-sends that invite's own link.
   *
   * NULL on any row raised before the rail existed, and on an information-only send that went as
   * an email attachment with no link at all (§14). The panel offers no resend for either, because
   * there is nothing left in the system that could reach the patient with them.
   */
  patientInviteId?: string | null;

  /**
   * §21.1 — UNREAD ON THE CLIENT, and kept in the DTO only because the server still serves them.
   *
   * They were the standalone rail's cooldown state. Nothing writes them now (`resendRequest` and
   * `mintDocumentToken` are gone) and the cooldown that governs a resend is the INVITE'S, so a
   * client reading `nextAllowedResendAt` off this row would be reading a computation over columns
   * that no longer move. The columns stay because dropping one is a migration with no benefit.
   */
  lastSentAt?: string | null;
  resendCount?: number;
  tokenExpiry?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;

  /** Computed server-side; drives the resend button's disabled state. */
  nextAllowedResendAt?: string | null;

  /**
   * WHETHER A RESEND WOULD BE REFUSED OUTRIGHT, rather than merely delayed — §21.1.
   *
   * `toStaffDto` computes `invite.active === true && !invite.completedAt`. It matters because
   * RETURNING A DOCUMENT COMPLETES THE INVITE THAT CARRIED IT
   * (`patientInviteController.js:561`), so any row still pinned to an invite the patient has
   * submitted has a dead link — and `resendInvite` refuses it 409 rather than spending a message
   * credit on a link the patient would be turned away from.
   *
   * THE SERVER HAS COMPUTED THIS SINCE PHASE 4 AND NO CLIENT READ IT. The panel drew a Resend
   * button on every such row and every click failed. See `tasks/document-rejection-resend.md` §7.
   *
   * OPTIONAL, and ABSENT MEANS PERMITTED (`!== false`, never `=== true`) — the same rule as
   * `canSignOff` / `canValidate`. A server that does not send it must not disable every resend in
   * the product.
   */
  inviteResendable?: boolean;

  PracticeDocument?: PatientDocumentRequestDocument | null;

  /**
   * The returned files. ONLY on `GET /patient-document-requests/:id` — the list endpoint sends
   * `returnedFileCount` alone, which is all a row needs.
   */
  returnedFiles?: PatientDocumentReturnedFileDto[] | null;

  /**
   * ACTIVE files only. See `PatientDocumentReturnedFileDto.active`: after a rejection this and
   * `returnedFiles.length` legitimately disagree, and the count is the one that means "pages the
   * practice currently holds".
   */
  returnedFileCount?: number;

  /* ── §26, STAGE TWO ───────────────────────────────────────────────────────────────── */

  /** Who put this on the clinical timeline. NEVER the same field as `reviewedByUserId`. */
  validatedByUserId?: string | null;
  validatedByName?: string | null;
  validatedAt?: string | null;

  /**
   * What the clinician observed. ENCRYPTED at rest and served ONLY by `GET /:id` — the list
   * endpoint strips it, exactly as it strips `reviewNote` and `aiSummary`.
   */
  validationNote?: string | null;
  /** Whether there IS one, which the list IS allowed to know. See `hasAiSummary`. */
  hasValidationNote?: boolean;

  /** The clinician's own attachments. `GET /:id` only, and never merged with `returnedFiles`. */
  validationFiles?: PatientDocumentReturnedFileDto[] | null;
  validationFileCount?: number;

  /**
   * WHAT THE SERVER SAYS THIS CALLER MAY DO, per document (§26).
   *
   * The panel hides buttons with these. They are NOT the enforcement — that is in the two
   * handlers, because a hidden button is a panel and not a permission — but shipping the answer
   * means the client never holds a second copy of the rule.
   *
   * OPTIONAL, and every reader must treat ABSENT as PERMITTED (`!== false`, never `=== true`). A
   * server that has not shipped §26 yet sends neither, and a client reading that as "forbidden"
   * would disable every review button in the product.
   */
  canSignOff?: boolean;
  canValidate?: boolean;
}

/*
 * `SendablePracticeDocument` USED TO LIVE HERE and is gone on purpose (§14.3, Phase 2.5c).
 *
 * It was a second, looser description of a row `PracticeDocumentDto` already describes — every
 * field optional, so `requiresResponse` would have arrived as `undefined` and read as "information
 * only" on a document the practice expects back. The shared picker holds `PracticeDocumentDto`,
 * which is what the store actually serves.
 */

/**
 * ONE SEND, N DOCUMENTS — §14.4, §14.5.
 *
 * `practiceDocumentIds` is an ARRAY and there is no singular field beside it, deliberately. 2.5b
 * accepts a lone `practiceDocumentId` as an array of one during the changeover, but a client that
 * kept both would have two ways to say the same thing and would eventually send them disagreeing.
 *
 * A send of one still goes out as an array of one, and still gets a `bundleId` (§16) — the reason
 * being the one worth repeating here: if a bundle were minted only where there were siblings, NULL
 * would mean two unrelated things.
 */
export interface CreateDocumentRequestPayload {
  patientId: string;
  practiceDocumentIds: string[];
  physicianId?: string | null;
  channels: string[];
}

/** §0 decision 3. A rejection without a reason is a rejection nobody can act on. */
export interface ReviewDocumentRequestPayload {
  reviewStatus: 'accepted' | 'rejected';
  reviewNote?: string | null;
}

/**
 * §26 STAGE TWO. Each field is named for the COLUMN it lands in.
 *
 * That is not a style choice. The 30-line note above `reviewRequest` in the controller records a
 * defect where the handler read `decision`/`note` while the only production caller posted
 * `reviewStatus`/`reviewNote`, and both suites stayed green because each tested its own half of
 * the mismatch. `validationNote` and `reviewNote` are two different columns and the two decisions
 * write to different ones — an observation is not a rejection reason.
 */
export interface ValidateDocumentRequestPayload {
  reviewStatus: 'validated' | 'rejected';
  /** What the clinician observed. `validated` only. */
  validationNote?: string | null;
  /** Why it is going back to the patient. `rejected` only, and required there. */
  reviewNote?: string | null;
}

/** The dedupe codes plus the refusals the picker is meant to make unreachable. */
export interface DocumentRequestFailure {
  code: string;
  message: string;
  /** True for the three §12 dedupe refusals — an answer, not a failure. */
  isDedupe: boolean;
  existingRequestId?: string | null;
  nextEligibleAt?: string | null;
  nextAllowedResendAt?: string | null;
}

/**
 * Statuses that mean "this ask is still live". Three of them, not one — see the header.
 *
 * `delivered` is NOT here and must never be added: an information sheet that went out is a
 * completed fact, not something the practice is waiting on. §16 records that this terminality is
 * not schema-expressible — the database's `CK_` lists the value and says nothing about what it
 * means — so this constant and the server's `DOCUMENT_PAYLOAD.openStatuses` are where it lives.
 */
export const OPEN_DOCUMENT_STATUSES: PatientDocumentRequestStatus[] =
  ['scheduled', 'sent', 'in_progress'];

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: 'numeric', month: 'short', year: 'numeric',
};

/** `1 Mar 2026`, or `''` for anything unparseable — never "Invalid Date" on a screen. */
export function formatDocumentDate(value: string | Date | null | undefined): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, DATE_OPTIONS);
}

/**
 * The returned files, in the ONE field the server puts them in.
 *
 * An earlier draft read three candidate association aliases because the Phase 2 brief named the
 * endpoint and not the field. The server that landed uses none of them — it serves a MAPPED
 * `returnedFiles`, not a raw Sequelize include — so the tolerance never fired and only hid the
 * break from review. One field, and a reviewer whose file list is empty is looking at a real defect
 * rather than at a client quietly guessing.
 */
export function returnedFiles(request: PatientDocumentRequestDto | null | undefined)
  : PatientDocumentReturnedFileDto[] {
  if (!request || !Array.isArray(request.returnedFiles)) return [];
  return request.returnedFiles;
}

/** The files that still stand. A rejection deactivates the previous generation. */
export function activeReturnedFiles(request: PatientDocumentRequestDto | null | undefined)
  : PatientDocumentReturnedFileDto[] {
  return returnedFiles(request).filter((file) => file.active !== false);
}

/**
 * §26. The clinician's own attachments that are still live.
 *
 * A separate accessor from `activeReturnedFiles` because they come from a separate TABLE and a
 * separate person. What the patient sent and what the practice added must never end up in one
 * list: on a clinical record, provenance is part of the content.
 */
export function activeValidationFiles(request: PatientDocumentRequestDto | null | undefined)
  : PatientDocumentReturnedFileDto[] {
  const files = request && Array.isArray(request.validationFiles) ? request.validationFiles : [];
  return files.filter((file) => file.active !== false);
}

/** What a rejection superseded. Shown separately, never mixed in with the live ones. */
export function supersededReturnedFiles(request: PatientDocumentRequestDto | null | undefined)
  : PatientDocumentReturnedFileDto[] {
  return returnedFiles(request).filter((file) => file.active === false);
}

/**
 * Turn an `HttpErrorResponse` from the send / resend / revoke / review endpoints into something a
 * practice user can act on.
 *
 * THE THREE DEDUPE CODES GET THREE DIFFERENT SENTENCES, for the reason the forms service records:
 * each means something different about what the practice should do next, and two of them mean the
 * practice should do nothing at all. A single "could not send" toast across all three sends a
 * receptionist to the phone to chase a document the patient already has.
 */
export function describeDocumentFailure(error: any, documentName?: string): DocumentRequestFailure {
  const body = error && error.error ? error.error : error;
  const code: string = (body && body.error) || 'UNKNOWN';
  const name = documentName ? `"${documentName}"` : 'this document';
  const existingRequestId = (body && body.existingRequestId) || null;
  const nextEligibleAt = (body && body.nextEligibleAt) || null;
  const nextAllowedResendAt = (body && body.nextAllowedResendAt) || null;

  switch (code) {
    case 'OPEN_REQUEST':
      return {
        code, isDedupe: true, existingRequestId, nextEligibleAt,
        message: `They already have ${name} outstanding. Resend the existing request instead of `
          + `creating a second one.`,
      };
    /*
     * §30 — `WITHIN_REPEAT_WINDOW` and `COMPLETED_NO_REPEAT` STOOD HERE AND ARE GONE.
     *
     * The API no longer refuses a send because the patient answered before: how often to ask is the
     * practice's workflow, and `repeatAfterDays` has been dropped from the schema. `OPEN_REQUEST`
     * above is the only dedupe code left, and it is a different claim — "there is a live link for
     * this right now", not "you have asked too often".
     *
     * Kept briefly as a rolling-deploy cushion, then deleted outright: neither documents nor forms
     * have ever been deployed to production, so no server anywhere can emit them.
     */
    case 'NOT_A_SIGN_OFF_USER':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: `Only the people named on ${name} can sign it off. Change who can in `
          + `Settings > Documents.`,
      };
    case 'NOT_A_VALIDATION_USER':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: `Only the people named on ${name} can validate it onto the timeline. Change who `
          + `can in Settings > Documents.`,
      };
    case 'NOT_SIGNED_OFF':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: `${name} has not been signed off yet. Somebody has to check it is the right `
          + `document before it can go on the timeline.`,
      };
    case 'ALREADY_VALIDATED':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: `${name} is already on the patient's timeline. Reopen the row to see it.`,
      };
    case 'ALREADY_REVIEWED':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: `${name} has already been signed off and is waiting for clinical validation.`,
      };
    case 'VALIDATION_NOTE_TOO_LONG':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: 'That observation is too long. Shorten it and try again.',
      };
    case 'UNSUPPORTED_FILE_TYPE':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: (body && body.message)
          || 'That file is not a PDF or a photograph. Attach one of those instead.',
      };
    case 'TOO_MANY_FILES':
    case 'FILES_TOO_LARGE':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: (body && body.message) || 'Those attachments are too large to send together.',
      };

    case 'MANUAL_SEND_DISABLED':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: `${documentName ? `"${documentName}"` : 'That document'} is not set up to be sent `
          + `by hand. Turn on manual sending in Settings > Documents.`,
      };
    case 'RESEND_COOLDOWN':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt, nextAllowedResendAt,
        message: 'This document was sent very recently. Please wait before sending it again.',
      };
    case 'ALREADY_COMPLETED':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: 'This document has already come back, so this request cannot be changed.',
      };
    case 'REVOKED':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: 'This request has been cancelled. Send a new one instead.',
      };
    // ── The four refusals `POST /:id/review` actually sends ────────────────────────
    case 'NOT_RETURNED':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: 'Nothing has come back from the patient yet, so there is nothing to review.',
      };
    case 'INVALID_DECISION':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: 'A review has to be either an acceptance or a rejection.',
      };
    case 'REVIEW_NOTE_REQUIRED':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: 'Say what is wrong with the document before sending it back.',
      };
    case 'REVIEW_NOTE_TOO_LONG':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: (body && body.message)
          || 'That note is too long. Please shorten it and try again.',
      };
    case 'PATIENT_NOT_FOUND':
    case 'DOCUMENT_NOT_FOUND':
    case 'REQUEST_NOT_FOUND':
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: (body && body.message) || 'That record no longer exists.',
      };
    default:
      return {
        code, isDedupe: false, existingRequestId, nextEligibleAt,
        message: (body && body.message)
          || 'There was a problem with this document request. Please try again.',
      };
  }
}

@Injectable({ providedIn: 'root' })
export class PatientDocumentRequestService {

  private apiUrl = 'api/patient-document-requests';

  private requestsSubject = new BehaviorSubject<PatientDocumentRequestDto[] | null>(null);
  /** The open patient's requests. `null` while loading — the panel shows a spinner. */
  patientDocumentRequests$ = this.requestsSubject.asObservable();

  /**
   * "Open this request" — emitted when the send dialog is refused by dedupe and the user asks to
   * be taken to the request that blocked it.
   *
   * Routed through the service rather than through `patient-file.component.ts`, so that the
   * sidebar action and the panel's own Send button both land in the same place without the patient
   * file needing to know anything about document requests.
   */
  private focusRequestSubject = new Subject<string>();
  focusRequest$ = this.focusRequestSubject.asObservable();

  private patientId?: string;

  constructor(
    private http: HttpClient,
    private patientService: PatientService,
    private socketService: SocketService,
  ) {
    this.subscribeToCurrentPatient();
    this.setupSocketListeners();
  }

  private authOptions() {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }

  private subscribeToCurrentPatient(): void {
    this.patientService.currentPatient$.pipe(
      switchMap((patient: any) => {
        if (patient) {
          this.patientId = patient.id;
          // `null`, not `[]`: "we have not looked yet" and "there are none" are different facts,
          // and the panel draws a spinner for the first and an empty message for the second.
          this.requestsSubject.next(null);
          return this.loadRequestsByPatient(patient.id);
        }
        this.patientId = undefined;
        this.requestsSubject.next(null);
        return [];
      }),
      tap((result: any) => {
        this.requestsSubject.next(result && result.data ? result.data : []);
      }),
    ).subscribe({
      error: () => this.requestsSubject.next([]),
    });
  }

  /**
   * The FOUR group-room events this feature emits, all from Phase 2b.
   *
   *   patientDocumentRequested  { id, patientId, practiceDocumentId, status }
   *   patientDocumentReturned   { id, patientId, practiceDocumentId, status, returnedAt,
   *                               reviewStatus, fileCount }
   *   patientDocumentRevoked    { id, patientId, status }
   *   patientDocumentReviewed   { id, patientId, status, reviewStatus, reviewedAt }
   *
   * It was FIVE. Phase 6 added `patientDocumentAiRead`; the server no longer emits it and nothing
   * listens — see the note at the foot of this method.
   *
   * ── WHY ONE OF THEM RELOADS AND THREE PATCH ──────────────────────────────────────────────
   *
   * `patientDocumentRequested` carries NO `PracticeDocument` include and no `createdAt`/`sentAt`,
   * so splicing it into the list would draw a row with a blank name and a blank date. The forms
   * service re-reads on its equivalent for exactly this reason; so does this one. The other three
   * carry everything that changed, so they patch in place and leave an open detail row alone.
   *
   * ── THE ONE THING `patientDocumentReviewed` DOES NOT SAY ─────────────────────────────────
   *
   * A REJECTION RE-OPENS THE SAME REQUEST — `status` goes back to `'sent'`, `returnedAt` is
   * cleared, `reviewStatus` stays `'rejected'` and the returned files are deactivated. The payload
   * carries the new `status` but NOT `returnedAt: null`, and a patch that only merges the keys it
   * is given would leave the old `returnedAt` on the row — so the panel would go on printing
   * "Returned 3 September" against an ask the patient has not answered yet. It is cleared here,
   * explicitly, from the same fact the server used: a reviewed row that is no longer `completed`
   * has nothing returned against it.
   */
  private setupSocketListeners(): void {
    this.socketService.listen('patientDocumentRequested', 'patient-document-request-service')
      .subscribe((event: any) => {
        if (!event || !this.isCurrentPatient(event.patientId)) return;
        this.refresh();
      });

    this.socketService.listen('patientDocumentReturned', 'patient-document-request-service')
      .subscribe((event: any) => {
        if (!event || !this.isCurrentPatient(event.patientId)) return;
        const patched = this.patchRequest(event.id, {
          status: event.status,
          returnedAt: event.returnedAt,
          reviewStatus: event.reviewStatus,
          returnedFileCount: event.fileCount,
        });
        // A return for a request we have never seen (raised on another screen while this patient
        // was open) still belongs on the list.
        if (!patched) this.refresh();
      });

    this.socketService.listen('patientDocumentRevoked', 'patient-document-request-service')
      .subscribe((event: any) => {
        if (!event || !this.isCurrentPatient(event.patientId)) return;
        this.patchRequest(event.id, { status: event.status || 'revoked' });
      });

    /*
     * §26 STAGE TWO. Success only — a stage-two REJECTION emits `patientDocumentReviewed`
     * instead, deliberately, so the re-open inference below is not written twice.
     */
    this.socketService.listen('patientDocumentValidated', 'patient-document-request-service')
      .subscribe((event: any) => {
        if (!event || !this.isCurrentPatient(event.patientId)) return;
        this.patchRequest(event.id, {
          status: event.status,
          reviewStatus: event.reviewStatus,
          validatedAt: event.validatedAt,
          validationFileCount: event.validationFileCount,
        });
      });

    this.socketService.listen('patientDocumentReviewed', 'patient-document-request-service')
      .subscribe((event: any) => {
        if (!event || !this.isCurrentPatient(event.patientId)) return;
        const patch: Partial<PatientDocumentRequestDto> = {
          status: event.status,
          reviewStatus: event.reviewStatus,
          reviewedAt: event.reviewedAt,
        };
        // See the header: a rejection re-opened the ask, and the payload does not say so in words.
        if (event.status && event.status !== 'completed') {
          patch.returnedAt = null;
          patch.returnedFileCount = 0;
          // §26 — the server cleared these on the row, so the held copy must lose them too, or
          // a re-opened ask keeps rendering a validator and an observation about pages that have
          // just been deactivated.
          patch.validatedByUserId = null;
          patch.validatedByName = null;
          patch.validatedAt = null;
          patch.hasValidationNote = false;
          patch.validationFileCount = 0;
        }
        this.patchRequest(event.id, patch);
      });

    /*
     * `patientDocumentAiRead` WAS LISTENED FOR HERE, and both halves of that mechanism are gone —
     * 9 Sep 2026, owner's call.
     *
     * It patched `hasAiSummary` and the two score columns onto the row so the patient-file panel's
     * "AI SUMMARY" block could stop saying the read was missing when it landed seconds after the
     * return. That block has been removed from the panel entirely — the read is for Cura, which
     * loads its own context when it needs it — so the patch had no consumer left on this screen.
     *
     * THE SERVER'S EMIT WENT WITH IT (`services/patientDocumentAiService.js`). An event with a
     * listener that patches a field nothing draws is `lessons.md` §5b — computed, then discarded —
     * and keeping the emit "for a future consumer" is how the next reader concludes something is
     * listening. `verify-document-ai.js` §8 carries the INVERTED assertion (§23): it used to prove
     * the event was emitted, and now proves nothing emits it.
     *
     * The FOUR events above are untouched. They carry the lifecycle a practice user is watching.
     */
  }

  private isCurrentPatient(patientId: string | undefined): boolean {
    return !!patientId && !!this.patientId
      && String(patientId).toLowerCase() === String(this.patientId).toLowerCase();
  }

  /** Merge a partial into one row. Returns false when the row is not on the list. */
  private patchRequest(id: string, patch: Partial<PatientDocumentRequestDto>): boolean {
    const current = this.requestsSubject.getValue();
    if (!current || !id) return false;
    let found = false;
    const next = current.map((request) => {
      if (request.id !== id) return request;
      found = true;
      const merged: PatientDocumentRequestDto = { ...request };
      // Undefined fields on a partial must not blank out what we already hold. `null` DOES
      // get through, and has to: `patientDocumentReviewed` clears `returnedAt` that way when a
      // rejection re-opens the ask.
      const keys = Object.keys(patch) as Array<keyof PatientDocumentRequestDto>;
      for (const key of keys) {
        const value = patch[key];
        if (value !== undefined) (merged as any)[key] = value;
      }
      return merged;
    });
    if (found) this.requestsSubject.next(next);
    return found;
  }

  /** Re-read the open patient's list from the server. */
  refresh(): void {
    if (!this.patientId) return;
    this.loadRequestsByPatient(this.patientId).subscribe({
      next: (result: any) => this.requestsSubject.next(result && result.data ? result.data : []),
      error: () => { /* leave the list as it stands rather than blanking a populated panel */ },
    });
  }

  /** Ask the panel to open one request. See `focusRequest$`. */
  focusRequest(id: string): void {
    if (id) this.focusRequestSubject.next(id);
  }

  /* ── HTTP ─────────────────────────────────────────────────────────────────────────────── */

  loadRequestsByPatient(patientId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/patient/${patientId}`, this.authOptions());
  }

  /**
   * One request WITH the files the patient returned.
   *
   * `groupId` is REQUIRED — `tasks/practice-documents.md` §32. The server puts it in the WHERE
   * rather than fetching the row and checking afterwards, so a caller must name the practice it
   * believes this request belongs to. Every caller has one in hand: the patient's, or (from the
   * Inbox) the practice whose worklist the row came off.
   */
  loadRequestById(id: string, groupId: string): Observable<any> {
    const options = this.authOptions() as any;
    return this.http.get<any>(`${this.apiUrl}/${id}`, {
      ...options,
      params: new HttpParams().set('groupId', groupId ? String(groupId) : ''),
    });
  }

  createRequest(payload: CreateDocumentRequestPayload): Observable<any> {
    return this.http.post<any>(this.apiUrl, payload, this.authOptions())
      .pipe(tap(() => this.refresh()));
  }

  /*
   * `resendRequest()` USED TO LIVE HERE and is gone — §21.1.
   *
   * It posted to `POST /patient-document-requests/:id/resend`, which MINTED A NEW TOKEN, so the
   * link already in the patient's inbox stopped working. That endpoint is deleted with the
   * standalone rail: a document now rides a `PatientInvite`, the link addresses the PATIENT rather
   * than one document, and a resend is `resendInvite` re-sending the same link.
   *
   * The panel therefore calls `PatientInviteService.resendInvite(request.patientInviteId)`. There
   * is deliberately no wrapper for it here — a second way to reach the same endpoint is a second
   * place for the two to disagree about the cooldown.
   */

  revokeRequest(id: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/${id}/revoke`, {}, this.authOptions())
      .pipe(tap((result: any) => {
        if (result && result.data) this.patchRequest(id, result.data);
      }));
  }

  /**
   * ACCEPT, or REJECT WITH A NOTE — §0 decision 3.
   *
   * The response is patched onto the row rather than triggering a reload: it carries the whole
   * updated request, and a reload would collapse the expanded row the reviewer is looking at.
   */
  reviewRequest(id: string, payload: ReviewDocumentRequestPayload): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/${id}/review`, payload, this.authOptions())
      .pipe(tap((result: any) => {
        if (result && result.data) this.patchRequest(id, result.data);
      }));
  }

  /**
   * THE PRACTICE FILES THE RETURN ITSELF — `tasks/document-rejection-resend.md` §7.
   *
   * The patient emailed the form back, walked in with a printed copy, or is simply never going to
   * use the link. Reception scans it onto the pending request and the ask is ANSWERED, rather than
   * chased for ever or revoked — which would record that it was withdrawn, not completed.
   *
   * It is the SAME return the patient's own endpoint files: the row reaches `completed` identically
   * and §26's two review stages happen exactly as they would have. What differs is recorded on the
   * row (`returnSource: 'practice'`, `returnedByUserId`) rather than hidden.
   *
   * MULTIPART, and NO `Content-Type` HEADER IS SET — load-bearing rather than an omission: setting
   * it by hand drops the boundary the browser would have generated and the server parses no file at
   * all. The parts are named `documentPage0`, `documentPage1`, … but the server reads `req.files`
   * through `upload.any()`, so the names are for the network log rather than for the parser.
   *
   * Patches from the response rather than refreshing: the reviewer is usually looking at the row.
   */
  fileReturn(id: string, files: File[]): Observable<any> {
    const form = new FormData();
    files.forEach((file, index) => form.append(`documentPage${index}`, file, file.name));

    return withPdfPages$(form, { dpi: 200 })
      .pipe(switchMap((body) => this.http.post<any>(`${this.apiUrl}/${id}/return`, body, this.authOptions())))
      .pipe(tap((result: any) => {
        if (result && result.data) this.patchRequest(id, result.data);
      }));
  }

  /**
   * §26 STAGE TWO — validate onto the timeline, or send it back to the patient.
   *
   * MULTIPART, because a validation may carry images. The metadata rides as a single `data` JSON
   * part beside `validationMedia0`, `validationMedia1`, … — the idiom `practice-document.service`
   * and `patient-risk-item.service` already use.
   *
   * NO `Content-Type` HEADER IS SET, and that is load-bearing rather than an omission: setting it
   * by hand drops the multipart boundary the browser would have generated, and the server then
   * parses no file at all. `authOptions()` carries only `Authorization`, so it is safe as it is —
   * see the note at the top of `practice-document.service.ts`.
   *
   * Patches from the response rather than refreshing, as `reviewRequest` does: a refresh would
   * collapse the expanded row the clinician is reading.
   */
  validateRequest(
    id: string,
    payload: ValidateDocumentRequestPayload,
    files: File[] = [],
  ): Observable<any> {
    const form = new FormData();
    form.append('data', JSON.stringify(payload));
    files.forEach((file, index) => form.append(`validationMedia${index}`, file, file.name));

    return withPdfPages$(form, { dpi: 200 })
      .pipe(switchMap((body) => this.http.post<any>(`${this.apiUrl}/${id}/validate`, body, this.authOptions())))
      .pipe(tap((result: any) => {
        if (result && result.data) this.patchRequest(id, result.data);
      }));
  }

  /*
   * `loadSendableDocuments` USED TO LIVE HERE and is gone on purpose (§14.3, Phase 2.5c).
   *
   * The blank store now has exactly ONE reader for "which documents can ride along with this" —
   * `PracticeDocumentService.listAttachableDocuments`, which the shared picker calls. Two clients
   * for the same endpoint is two places to decide what `active` means and what a `groupId` is for,
   * and the shared picker exists precisely so that decision is made once. `documentsUrl` went with
   * it: this service speaks to the request lifecycle, not to the store.
   */
}
